import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const VitalInput = ({ id, label, unit, value, onChange, placeholder, testId }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
      {label} <span className="text-slate-400 normal-case">{unit}</span>
    </Label>
    <Input
      id={id} data-testid={testId} inputMode="decimal"
      className="font-mono text-lg h-12"
      value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default function VitalsCard({ vitals, onChange }) {
  const set = (k, v) => onChange({ ...vitals, [k]: v });
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6">
      <h3 className="font-heading text-lg font-semibold mb-4">Parametri vitali</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <VitalInput id="pa" label="PA" unit="mmHg" placeholder="120/80" value={vitals.pa} onChange={(v) => set("pa", v)} testId="input-vital-pa" />
        <VitalInput id="fc" label="FC" unit="bpm" value={vitals.fc} onChange={(v) => set("fc", v)} testId="input-vital-fc" />
        <VitalInput id="fr" label="FR" unit="/min" value={vitals.fr} onChange={(v) => set("fr", v)} testId="input-vital-fr" />
        <VitalInput id="spo2" label="SpO₂" unit="%" value={vitals.spo2} onChange={(v) => set("spo2", v)} testId="input-vital-spo2" />
        <VitalInput id="temp" label="Temperatura" unit="°C" value={vitals.temp} onChange={(v) => set("temp", v)} testId="input-vital-temp" />
        <VitalInput id="glicemia" label="Glicemia" unit="mg/dL" value={vitals.glicemia} onChange={(v) => set("glicemia", v)} testId="input-vital-glicemia" />
      </div>
      <div className="flex items-center justify-between mt-5 p-3 rounded-md bg-slate-50 border border-slate-200">
        <div>
          <div className="text-sm font-medium">Ossigenoterapia</div>
          <div className="text-xs text-slate-500">Paziente in O₂ supplementare</div>
        </div>
        <Switch checked={!!vitals.o2} onCheckedChange={(v) => set("o2", v)} data-testid="switch-vital-o2" />
      </div>
      {vitals.o2 && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          <VitalInput id="o2_flow" label="Flusso O₂" unit="L/min" value={vitals.o2_flow} onChange={(v) => set("o2_flow", v)} testId="input-vital-o2-flow" />
          <VitalInput id="o2_device" label="Presidio" unit="" value={vitals.o2_device} onChange={(v) => set("o2_device", v)} testId="input-vital-o2-device" />
        </div>
      )}
      <details className="mt-5">
        <summary className="text-sm font-medium text-slate-700 cursor-pointer select-none">Valori opzionali (EGA, Hb, altro)</summary>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
          <VitalInput id="ph" label="EGA pH" unit="" value={vitals.ega_ph} onChange={(v) => set("ega_ph", v)} testId="input-vital-ega-ph" />
          <VitalInput id="pco2" label="EGA pCO₂" unit="mmHg" value={vitals.ega_pco2} onChange={(v) => set("ega_pco2", v)} testId="input-vital-ega-pco2" />
          <VitalInput id="po2" label="EGA pO₂" unit="mmHg" value={vitals.ega_po2} onChange={(v) => set("ega_po2", v)} testId="input-vital-ega-po2" />
          <VitalInput id="hco3" label="EGA HCO₃⁻" unit="mmol/L" value={vitals.ega_hco3} onChange={(v) => set("ega_hco3", v)} testId="input-vital-ega-hco3" />
          <VitalInput id="be" label="EGA Base Excess" unit="mmol/L" value={vitals.ega_be} onChange={(v) => set("ega_be", v)} testId="input-vital-ega-be" />
          <VitalInput id="sato2" label="EGA SatO₂" unit="%" value={vitals.ega_sato2} onChange={(v) => set("ega_sato2", v)} testId="input-vital-ega-sato2" />
          <VitalInput id="lact" label="Lattati" unit="mmol/L" value={vitals.lattati} onChange={(v) => set("lattati", v)} testId="input-vital-lattati" />
          <VitalInput id="k" label="EGA K⁺" unit="mEq/L" value={vitals.ega_k} onChange={(v) => set("ega_k", v)} testId="input-vital-ega-k" />
          <VitalInput id="hb" label="Hb" unit="g/dL" value={vitals.hb} onChange={(v) => set("hb", v)} testId="input-vital-hb" />
        </div>
      </details>
    </div>
  );
}
